import './data-editor-form.scss';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Column, EditType } from '../../../model/table';
import { TableEditControl } from '../table-edit-control/table-edit-contol';
import CustomButton from '../custom-button/custom-button';
import { useDebouncedValidation } from '../hooks/debounced-validation-hook';
import { EditorMode } from '../../../utils/enums';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import usePromiseFeedback from '../promise-feedback/usePromiseFeedback';
import Alert from '../alert/alert';

interface DataEditorFormProps<T> {
    formData: T;
    fieldsConfig: Column<T>[];
    onSave: (updatedRowData: T) => Promise<any>;
    close: () => void;
    dataChangesHandler?: ProxyHandler<any>;
    duplicationValidator?: (record: T, originalRecord: T | undefined, keys: string[]) => boolean;
    mode?: EditorMode;
    header: string;
    okBtnText: string;
    allData?: T[];
    InfoMsg?: React.FC<{ data: any }>;
    getDisableSaveAlert?: (data: T) => string | undefined;
}

export const DataEditorForm = <T, >({
                                      formData,
                                      fieldsConfig,
                                      onSave,
                                      close,
                                      dataChangesHandler,
                                      duplicationValidator,
                                      mode,
                                      header,
                                      okBtnText,
                                      allData,
                                      InfoMsg,
                                      getDisableSaveAlert
                                  }: DataEditorFormProps<T>) => {

    const formDataInitializer = (initializeData: T) => {
        return dataChangesHandler
            ? new Proxy(initializeData, dataChangesHandler)
            : initializeData;
    };

    const [originalFormData] = useState(formData);
    const [editedFormData, setEditedFormData] = React.useState(formDataInitializer({ ...formData }));
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [isDataDuplicated, setIsDataDuplicated] = useState(false);
    const [missingMandatoryFields, setMissingMandatoryFields] = useState<string[]>([]);
    const [validationErrors, setValidationErrors] = useState<Map<string, string>>(new Map());
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [disableSaveMessage, setDisableSaveMessage] = useState<string | undefined>(undefined);


    const prevFormDataRef = useRef<T>(formData);

    const debouncedValidateInput = useDebouncedValidation(fieldsConfig, setValidationErrors, setIsEditing, originalFormData, allData);
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();

    const editableFields = useMemo(() => fieldsConfig.filter(col => col.displayOnEdit).map(col => col.accessor), [fieldsConfig]);

    const mandatoryColumns = useMemo(() => {
        return fieldsConfig.filter(col =>
            typeof col.mandatory === 'function' ? col.mandatory?.(editedFormData) : col.mandatory);
        }, [fieldsConfig, editedFormData]);

    const dataKeys: string[] = useMemo(() => {
        return fieldsConfig.filter(column => column.isKeyField)?.map(keyField => keyField.accessor);
    }, [fieldsConfig]);
    const dataKeyCols: Column<T>[] = useMemo(() => {
        return fieldsConfig.filter(column => column.isKeyField);
    }, [fieldsConfig]);

    useEffect(() => {
        setEditedFormData(formDataInitializer({ ...formData }));
    }, [formData]);

    useEffect(() => {
        if (isEqual(editedFormData, formDataInitializer({ ...formData }))) {
            setIsDirty(false);
        }
        validateInputs();
        validateDuplication();
        updateMissingMandatoryFields();
        prevFormDataRef.current = { ...editedFormData };
        if (getDisableSaveAlert) {
            setDisableSaveMessage(getDisableSaveAlert(editedFormData));
        } else {
            setDisableSaveMessage(undefined);
        }
    }, [editedFormData]);

    useEffect(() => {
        setIsSaveDisabled(!!validationErrors.size ||
            !!missingMandatoryFields.length ||
            isEditing ||
            !isDirty ||
            isDataDuplicated ||
            !!disableSaveMessage);
    }, [validationErrors, missingMandatoryFields, isEditing, isDirty, isDataDuplicated, disableSaveMessage]);

    const handleSave = () => {
        setIsLoading(true);
        promiseFeedbackFactory({
            promise: onSave(editedFormData),
            successCallback: (res) => {
                close();
                setIsLoading(false);
            },
            errorCallback: (res) => {
                setIsLoading(false);
            },
            notifObj: { show: true }
        });
    };

    const handleCancel = () => {
        close();
    };

    const editProp = (accessor: string, value: any): void => {
        setIsEditing(true);
        editedFormData[accessor] = value;
        setEditedFormData(formDataInitializer({ ...editedFormData }));
        setIsDirty(true);
    };

    const updateMissingMandatoryFields = () => {
        const updatedMissingMandatoryFields =
            mandatoryColumns.filter(col => {
                const key = col.accessor;
                const value = editedFormData[key];
                return isEmpty(value) || (col.type === EditType.IP && value === '-');
            }).map(col => col.accessor);
        setMissingMandatoryFields(updatedMissingMandatoryFields);
    };

    const validateDuplication = () => {
        if (duplicationValidator) {
            const keyChanged = dataKeyCols.some((col) => {
                return !isEqual(editedFormData[col.accessor], (prevFormDataRef.current as any)[col.accessor]);
            });

            if (keyChanged) {
                setIsDataDuplicated(
                    duplicationValidator(
                        editedFormData,
                        mode === EditorMode.EDIT ? formData : undefined,
                        dataKeys
                    )
                );
            }
        }
    };

    const validateInputs = () => {
        editableFields.forEach((key) => {
            if (!isEqual(editedFormData[key], (prevFormDataRef.current as any)[key])) {
                setValidationErrors((prevErrors) => {
                    prevErrors.delete(key);
                    return new Map(prevErrors);
                });
                debouncedValidateInput(key, editedFormData[key]);
            }
        });
    };

    const hasError = (column: Column<T>) => {
        return validationErrors.get(column.accessor);
    };

    const hasDuplicationError = (column: Column<T>) => {
        return column.isKeyField && isDataDuplicated;
    };

    const getErrorClasses = (column: Column<T>) => {
        return `${hasError(column) ? 'has-error' : ''}${hasDuplicationError(column) ? 'has-duplication-error' : ''}`;
    };

    return (
        <div className="data-editor-form">
            <div className="form-header">{header}</div>
            {isDataDuplicated && <Alert header={'Duplicate values found'}
                message={'Please update any of the highlighted inputs and try again.'} />}
            {InfoMsg ? <InfoMsg data={editedFormData} /> : null}
            {disableSaveMessage && <Alert message={disableSaveMessage} />}
            <div className="form-fields">
                {fieldsConfig.filter(column => column.displayOnEdit).map(column => (
                    <div className={`form-field-wrapper ${column.header.toLowerCase()} ${getErrorClasses(column)}`} key={column.header}>
                        <span><span className="form-field-label">{column.header}</span>
                            {!column.mandatory && <span className="optional-label">Optional</span>}</span>
                        <span className={`form-field-input-wrapper ${getErrorClasses(column)}`}>
                            <TableEditControl
                                hasError={!!getErrorClasses(column)}
                                type={column.type}
                                column={column}
                                value={column.getValue?.(editedFormData) ?? editedFormData[column.accessor]}
                                placeholder={column.getPlaceholder?.(column, editedFormData)}
                                editProp={editProp}
                                disabled={column.disableEdit?.(formData)}
                                rowData={editedFormData}
                            />
                        </span>
                        <span className={'form-field-error'}>{validationErrors.get(column.accessor)}</span>
                        {column.description && <span className={`form-field-description ${column.header.toLowerCase()}`}>{column.description}</span>}
                    </div>
                ))}
            </div>
            <div className="form-buttons">
                <CustomButton className={'secondary-btn'} label={'Cancel'} onClick={handleCancel} />
                <CustomButton className={`primary-btn ${isLoading ? 'loading' : ''}`} label={okBtnText} onClick={handleSave} isDisabled={isSaveDisabled} />
            </div>
            {promiseContextHolder}
        </div>
    );
};